import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import moment from "moment"
import ReactFlow, {
	Background,
	Controls,
	MiniMap,
	ReactFlowProvider,
	addEdge,
	useEdgesState,
	useNodesState,
	useReactFlow,
} from "reactflow"
import {
	Save,
	Plus,
	Trash2,
	Inbox,
	Sparkles,
	GitBranch,
	Database,
	StickyNote,
	Maximize2,
	Minimize2,
	FileText,
	Copy,
	Download,
	X,
} from "lucide-react"
import "reactflow/dist/style.css"

import Button from "../../../components/ui/button/button"
import Input from "../../../components/ui/inputs/input"
import Textarea from "../../../components/ui/inputs/textarea"
import Select from "../../../components/ui/select/select"
import "./flow-canvas.scss"

import { NODE_TYPES, NODE_PALETTE, defaultNodeData } from "./flow-nodes"
import { EDGE_TYPES, FlowEdgeContext } from "./flow-edges"
import NodeDrawer from "./node-drawer"
import EdgeDrawer from "./edge-drawer"
import { buildFlowMarkdown } from "./flow-markdown"
import {
	createFlow,
	deleteFlow,
	listFlows,
	loadFlowOrSeed,
	saveFlow,
} from "./flow-storage"
import { SUPPLIER_DOCUMENT_FLOW_ID } from "../_seeds/supplier-document-flow"

/**
 * AI Tooling flow canvas.
 *
 * A manager (`FlowCanvas`) lets you pick between flows, create a new freeform
 * flow (name + optional description), or delete one. The selected flow renders
 * on a top-down dotted board (`FlowBoard`): drop in any node type from the
 * palette, drag them around freely (no connections required), wire nodes by
 * clicking a bottom handle then a top handle, click a connection to select and
 * delete it, and click a node to edit it in the right-hand drawer. Graph +
 * positions persist to `ai_flows/{flowId}` via the explicit Save button.
 */

const PALETTE_ICONS = {
	trigger: Inbox,
	agent: Sparkles,
	router: GitBranch,
	step: Database,
	note: StickyNote,
}

// Human-readable label for an edge's routing condition.
const conditionLabel = (data) => {
	const c = data?.condition
	if (!c || c.kind === "always") return data?.note || ""
	if (c.kind === "classification") return c.value || "classification"
	if (c.kind === "content_type") return `content: ${c.value}`
	if (c.kind === "custom") return c.value || data?.note || ""
	return data?.note || ""
}

const DEFAULT_EDGE_OPTIONS = {
	type: "flow", // custom deletable bezier (see flow-edges.js)
	markerEnd: { type: "arrowclosed", width: 16, height: 16, color: "#94a3b8" },
	style: { stroke: "#94a3b8", strokeWidth: 1.5 },
}

// Decorate stored/seeded edges with the custom type + styling + rendered label.
// `defaultEdgeOptions` only reliably covers newly-connected edges, so we bake
// the visual + type onto every edge here to be sure the deletable custom edge
// is used for the loaded graph too.
const decorateEdge = (edge) => ({
	...DEFAULT_EDGE_OPTIONS,
	...edge,
	label: conditionLabel(edge.data),
})

const MINIMAP_NODE_COLOR = (node) => {
	switch (node.type) {
		case "trigger":
			return "#0ea5e9"
		case "router":
			return "#f59e0b"
		case "step":
			return "#10b981"
		case "note":
			return "#facc15"
		default:
			return node?.data?.status === "planned" ? "#c4b5fd" : "#6366f1"
	}
}

const makeNodeId = (type) =>
	`${type}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`

// -----------------------------------------------------------------------------
// Board (one flow)
// -----------------------------------------------------------------------------

function FlowBoard({ flowId, onMetaSaved }) {
	const [nodes, setNodes, onNodesChange] = useNodesState([])
	const [edges, setEdges, onEdgesChange] = useEdgesState([])

	const [loading, setLoading] = useState(true)
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState(null)
	const [dirty, setDirty] = useState(false)
	const [savedMeta, setSavedMeta] = useState(null)
	const [selectedId, setSelectedId] = useState(null)
	const [selectedEdgeId, setSelectedEdgeId] = useState(null)
	const [meta, setMeta] = useState({ label: "", description: "" })
	const [showSpec, setShowSpec] = useState(false)
	const [specMarkdown, setSpecMarkdown] = useState("")
	const [specLoading, setSpecLoading] = useState(false)

	const boardRef = useRef(null)
	const addCount = useRef(0)
	const rf = useReactFlow()

	useEffect(() => {
		let cancelled = false

		const init = async () => {
			setLoading(true)
			setError(null)
			try {
				const flow = await loadFlowOrSeed(flowId)
				if (cancelled) return
				setNodes(flow.nodes)
				setEdges(flow.edges.map(decorateEdge))
				setMeta({ label: flow.label || "", description: flow.description || "" })
				// A freshly-seeded (never-saved) flow starts dirty so the first
				// Save writes it; a loaded flow starts clean.
				setDirty(!flow.persisted)
				setSavedMeta(
					flow.persisted
						? { updated_at: flow.updated_at, updated_by: flow.updated_by }
						: null
				)
				if (flow.loadError) {
					setError(
						`Couldn't read the saved flow (${flow.loadError}). Showing the default layout - Save needs the ai_flows Firestore rule deployed.`
					)
				}
			} catch (err) {
				if (!cancelled) setError(err?.message || String(err))
			} finally {
				if (!cancelled) setLoading(false)
			}
		}

		init()
		return () => {
			cancelled = true
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [flowId])

	const handleNodesChange = useCallback(
		(changes) => {
			onNodesChange(changes)
			const meaningful = changes.some(
				(c) =>
					(c.type === "position" && c.dragging === false) ||
					c.type === "remove" ||
					c.type === "add"
			)
			if (meaningful) setDirty(true)
		},
		[onNodesChange]
	)

	const handleEdgesChange = useCallback(
		(changes) => {
			onEdgesChange(changes)
			if (changes.some((c) => c.type === "remove" || c.type === "add")) setDirty(true)
		},
		[onEdgesChange]
	)

	const onConnect = useCallback(
		(params) => {
			setEdges((eds) =>
				addEdge(
					{
						...DEFAULT_EDGE_OPTIONS,
						...params,
						data: { condition: { kind: "always" }, note: "" },
						label: "",
					},
					eds
				)
			)
			setDirty(true)
		},
		[setEdges]
	)

	const onNodeClick = useCallback((_event, node) => {
		setSelectedId(node.id)
		setSelectedEdgeId(null)
	}, [])
	const onEdgeClick = useCallback((_event, edge) => {
		setSelectedEdgeId(edge.id)
		setSelectedId(null)
	}, [])
	const onPaneClick = useCallback(() => {
		setSelectedId(null)
		setSelectedEdgeId(null)
	}, [])

	// Add a fresh node at the centre of the current viewport (with a little
	// jitter so repeated adds don't stack), then open its drawer for naming.
	const addNode = useCallback(
		(type) => {
			const bounds = boardRef.current?.getBoundingClientRect()
			let position = { x: 200, y: 160 }
			if (bounds) {
				const { x, y, zoom } = rf.getViewport()
				const jitter = (addCount.current++ % 6) * 26
				position = {
					x: (bounds.width / 2 - x) / zoom - 110 + jitter,
					y: (bounds.height / 2 - y) / zoom - 26 + jitter,
				}
			}
			const id = makeNodeId(type)
			setNodes((nds) => nds.concat({ id, type, position, data: defaultNodeData(type) }))
			setDirty(true)
			setSelectedId(id)
		},
		[rf, setNodes]
	)

	const onDeleteEdge = useCallback(
		(edgeId) => {
			setEdges((es) => es.filter((e) => e.id !== edgeId))
			setSelectedEdgeId((current) => (current === edgeId ? null : current))
			setDirty(true)
		},
		[setEdges]
	)

	// Patch an edge's routing condition/note from the edge drawer and recompute
	// its rendered label so the canvas updates immediately.
	const onUpdateEdge = useCallback(
		(edgeId, dataPatch) => {
			setEdges((es) =>
				es.map((e) => {
					if (e.id !== edgeId) return e
					const data = { ...e.data, ...dataPatch }
					return { ...e, data, label: conditionLabel(data) }
				})
			)
			setDirty(true)
		},
		[setEdges]
	)

	const onDeleteNode = useCallback(
		(nodeId) => {
			setNodes((nds) => nds.filter((n) => n.id !== nodeId))
			setEdges((es) => es.filter((e) => e.source !== nodeId && e.target !== nodeId))
			setSelectedId(null)
			setDirty(true)
		},
		[setNodes, setEdges]
	)

	const onUpdateNode = useCallback(
		(nodeId, dataPatch) => {
			setNodes((nds) =>
				nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, ...dataPatch } } : n))
			)
			setDirty(true)
		},
		[setNodes]
	)

	const selectedNode = useMemo(
		() => nodes.find((n) => n.id === selectedId) || null,
		[nodes, selectedId]
	)

	const selectedEdge = useMemo(
		() => edges.find((e) => e.id === selectedEdgeId) || null,
		[edges, selectedEdgeId]
	)
	const selectedEdgeSource = useMemo(
		() => (selectedEdge ? nodes.find((n) => n.id === selectedEdge.source) || null : null),
		[nodes, selectedEdge]
	)
	const selectedEdgeTarget = useMemo(
		() => (selectedEdge ? nodes.find((n) => n.id === selectedEdge.target) || null : null),
		[nodes, selectedEdge]
	)

	const edgeCtx = useMemo(() => ({ onDeleteEdge }), [onDeleteEdge])

	// Generate the live markdown writeup of the current flow.
	const openSpec = useCallback(async () => {
		setShowSpec(true)
		setSpecLoading(true)
		try {
			const markdown = await buildFlowMarkdown({
				label: meta.label,
				description: meta.description,
				nodes,
				edges,
			})
			setSpecMarkdown(markdown)
		} catch (err) {
			setSpecMarkdown(`Could not generate spec: ${err?.message || String(err)}`)
		} finally {
			setSpecLoading(false)
		}
	}, [meta, nodes, edges])

	const save = async () => {
		setSaving(true)
		setError(null)
		try {
			await saveFlow(flowId, {
				nodes,
				edges,
				label: meta.label,
				description: meta.description,
			})
			setSavedMeta({ updated_at: { seconds: Math.floor(Date.now() / 1000) } })
			setDirty(false)
			onMetaSaved?.(flowId, meta)
		} catch (err) {
			setError(err?.message || String(err))
		} finally {
			setSaving(false)
		}
	}

	const lastSavedLabel = useMemo(() => {
		const seconds = savedMeta?.updated_at?.seconds ?? savedMeta?.updated_at?._seconds ?? null
		if (!seconds) return null
		return moment.unix(seconds).format("D MMM YYYY [at] HH:mm")
	}, [savedMeta])

	if (loading) {
		return <div className="ai-tooling-loading">Loading flow...</div>
	}

	return (
		<div className="ai-flow">
			<div className="ai-flow-toolbar">
				<div className="ai-flow-toolbar-status">
					{dirty ? (
						<span className="ai-flow-dirty">Unsaved changes</span>
					) : lastSavedLabel ? (
						<span className="ai-flow-saved">Saved {lastSavedLabel}</span>
					) : (
						<span className="ai-flow-saved">No changes</span>
					)}
				</div>
				<div className="ai-flow-toolbar-actions">
					<Button
						type="OUTLINE"
						small={true}
						label="Spec (MD)"
						icon={<FileText size={14} />}
						onClick={openSpec}
					/>
					<Button
						label="Save layout"
						icon={<Save size={14} />}
						loading={saving}
						loadingText="Saving..."
						onClick={save}
						disabled={saving || !dirty}
					/>
				</div>
			</div>

			{error && (
				<div className="ai-panel-error" role="alert">
					{error}
				</div>
			)}

			<div className="ai-flow-board" ref={boardRef}>
				<FlowEdgeContext.Provider value={edgeCtx}>
					<ReactFlow
						nodes={nodes}
						edges={edges}
						onNodesChange={handleNodesChange}
						onEdgesChange={handleEdgesChange}
						onConnect={onConnect}
						onNodeClick={onNodeClick}
						onEdgeClick={onEdgeClick}
						onPaneClick={onPaneClick}
						nodeTypes={NODE_TYPES}
						edgeTypes={EDGE_TYPES}
						defaultEdgeOptions={DEFAULT_EDGE_OPTIONS}
						deleteKeyCode={["Delete", "Backspace"]}
						fitView
						fitViewOptions={{ padding: 0.2 }}
						minZoom={0.3}
						maxZoom={1.75}
						proOptions={{ hideAttribution: true }}>
						<Background variant="dots" gap={20} size={1} color="#cbd5e1" />
						<Controls showInteractive={false} />
						<MiniMap pannable zoomable nodeColor={MINIMAP_NODE_COLOR} nodeStrokeWidth={2} />
					</ReactFlow>
				</FlowEdgeContext.Provider>

				<div className="ai-flow-palette">
					<span className="ai-flow-palette-label">Add node</span>
					{NODE_PALETTE.map(({ type, label }) => {
						const Icon = PALETTE_ICONS[type] || Sparkles
						return (
							<button
								key={type}
								type="button"
								className={`ai-flow-palette-btn type-${type}`}
								onClick={() => addNode(type)}
								title={`Add ${label.toLowerCase()} node`}>
								<Icon size={14} />
								<span>{label}</span>
							</button>
						)
					})}
				</div>

				{selectedNode ? (
					<NodeDrawer
						node={selectedNode}
						onClose={() => setSelectedId(null)}
						onUpdateNode={onUpdateNode}
						onDeleteNode={onDeleteNode}
					/>
				) : selectedEdge ? (
					<EdgeDrawer
						edge={selectedEdge}
						sourceNode={selectedEdgeSource}
						targetNode={selectedEdgeTarget}
						onClose={() => setSelectedEdgeId(null)}
						onUpdateEdge={onUpdateEdge}
						onDeleteEdge={onDeleteEdge}
					/>
				) : null}
			</div>

			{showSpec && (
				<SpecModal
					markdown={specMarkdown}
					loading={specLoading}
					fileName={`${flowId}.md`}
					onClose={() => setShowSpec(false)}
				/>
			)}
		</div>
	)
}

// -----------------------------------------------------------------------------
// Flow spec (markdown) modal
// -----------------------------------------------------------------------------

function SpecModal({ markdown, loading, fileName, onClose }) {
	const [copied, setCopied] = useState(false)

	const copy = async () => {
		try {
			await navigator.clipboard.writeText(markdown)
			setCopied(true)
			setTimeout(() => setCopied(false), 1500)
		} catch (err) {
			// Clipboard can be blocked (permissions / insecure context) - ignore.
		}
	}

	const download = () => {
		const blob = new Blob([markdown], { type: "text/markdown;charset=utf-8" })
		const url = URL.createObjectURL(blob)
		const link = document.createElement("a")
		link.href = url
		link.download = fileName || "flow.md"
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
		URL.revokeObjectURL(url)
	}

	return (
		<div className="ai-flow-modal-backdrop" onMouseDown={onClose}>
			<div className="ai-flow-spec-modal" onMouseDown={(event) => event.stopPropagation()}>
				<header className="ai-flow-spec-header">
					<h3>Flow spec (Markdown)</h3>
					<div className="ai-flow-spec-actions">
						<Button
							type="OUTLINE"
							small={true}
							label={copied ? "Copied" : "Copy"}
							icon={<Copy size={13} />}
							onClick={copy}
							disabled={loading}
						/>
						<Button
							type="OUTLINE"
							small={true}
							label="Download .md"
							icon={<Download size={13} />}
							onClick={download}
							disabled={loading}
						/>
						<button
							type="button"
							className="ai-flow-drawer-close"
							onClick={onClose}
							aria-label="Close">
							<X size={18} />
						</button>
					</div>
				</header>
				<div className="ai-flow-spec-body">
					{loading ? (
						<p className="ai-flow-drawer-hint">Generating spec...</p>
					) : (
						<pre className="ai-flow-spec-pre">{markdown}</pre>
					)}
				</div>
			</div>
		</div>
	)
}

// -----------------------------------------------------------------------------
// Create-flow modal
// -----------------------------------------------------------------------------

function CreateFlowModal({ name, description, onName, onDescription, onCancel, onCreate, creating }) {
	return (
		<div className="ai-flow-modal-backdrop" onMouseDown={onCancel}>
			<div className="ai-flow-modal" onMouseDown={(e) => e.stopPropagation()}>
				<h3>New flow</h3>
				<p className="ai-flow-modal-sub">
					Prototype anything - the nodes don't have to map to a real tool.
				</p>
				<div className="ai-flow-modal-field">
					<Input
						type="text"
						placeholder="Flow name:"
						value={name}
						onChange={onName}
						showLabel
					/>
				</div>
				<div className="ai-flow-modal-field">
					<Textarea
						placeholder="Description (optional):"
						value={description}
						onChange={onDescription}
						showLabel
						rows={3}
					/>
				</div>
				<div className="ai-flow-modal-actions">
					<Button type="OUTLINE" label="Cancel" onClick={onCancel} disabled={creating} />
					<Button
						label="Create flow"
						icon={<Plus size={14} />}
						loading={creating}
						loadingText="Creating..."
						onClick={onCreate}
						disabled={creating || !name.trim()}
					/>
				</div>
			</div>
		</div>
	)
}

// -----------------------------------------------------------------------------
// Manager (flow picker + create/delete)
// -----------------------------------------------------------------------------

function FlowCanvas({ initialFlowId = SUPPLIER_DOCUMENT_FLOW_ID }) {
	const [flows, setFlows] = useState([])
	const [flowId, setFlowId] = useState(initialFlowId)
	const [listError, setListError] = useState(null)
	const [showCreate, setShowCreate] = useState(false)
	const [creating, setCreating] = useState(false)
	const [newName, setNewName] = useState("")
	const [newDesc, setNewDesc] = useState("")
	const [fullscreen, setFullscreen] = useState(false)

	const refreshList = useCallback(async (preferId) => {
		const { flows: list, error } = await listFlows()
		setFlows(list)
		setListError(error || null)
		setFlowId((current) => {
			const wanted = preferId || current
			if (wanted && list.some((f) => f.id === wanted)) return wanted
			const seedFlow = list.find((f) => f.id === SUPPLIER_DOCUMENT_FLOW_ID)
			return seedFlow?.id || list[0]?.id || null
		})
	}, [])

	useEffect(() => {
		refreshList(initialFlowId)
	}, [refreshList, initialFlowId])

	// Esc exits full screen - but not while typing in a field or the JSON editor.
	useEffect(() => {
		if (!fullscreen) return undefined
		const onKey = (event) => {
			if (event.key !== "Escape") return
			const target = event.target
			const tag = target?.tagName
			if (
				tag === "INPUT" ||
				tag === "TEXTAREA" ||
				target?.isContentEditable ||
				target?.closest?.(".cm-editor")
			)
				return
			setFullscreen(false)
		}
		window.addEventListener("keydown", onKey)
		return () => window.removeEventListener("keydown", onKey)
	}, [fullscreen])

	const flowOptions = useMemo(
		() => Object.fromEntries(flows.map((f) => [f.id, f.label])),
		[flows]
	)
	const currentFlow = useMemo(() => flows.find((f) => f.id === flowId) || null, [flows, flowId])

	const onMetaSaved = useCallback((id, savedMeta) => {
		setFlows((fs) =>
			fs.map((f) => (f.id === id ? { ...f, ...savedMeta, persisted: true } : f))
		)
	}, [])

	const createNew = async () => {
		if (!newName.trim()) return
		setCreating(true)
		setListError(null)
		try {
			const created = await createFlow({ label: newName, description: newDesc })
			await refreshList(created.id)
			setShowCreate(false)
			setNewName("")
			setNewDesc("")
		} catch (err) {
			setListError(err?.message || String(err))
		} finally {
			setCreating(false)
		}
	}

	const removeFlow = async () => {
		if (!flowId) return
		// eslint-disable-next-line no-alert
		if (!window.confirm(`Delete flow "${currentFlow?.label || flowId}"? This can't be undone.`))
			return
		setListError(null)
		try {
			await deleteFlow(flowId)
			await refreshList()
		} catch (err) {
			setListError(err?.message || String(err))
		}
	}

	return (
		<div className={["ai-flow-manager", fullscreen && "is-fullscreen"].filter(Boolean).join(" ")}>
			<div className="ai-flow-flowbar">
				<div className="ai-flow-flowbar-select">
					<Select
						showLabel
						placeholder="Flow:"
						value={flowId || ""}
						selected={flowId || ""}
						onSelect={(option) => setFlowId(option.option)}
						options={flowOptions}
					/>
				</div>
				<Button
					type="OUTLINE"
					small={true}
					label="New flow"
					icon={<Plus size={14} />}
					onClick={() => setShowCreate(true)}
				/>
				<Button
					type="OUTLINE"
					small={true}
					label="Delete"
					icon={<Trash2 size={13} />}
					onClick={removeFlow}
					disabled={!flowId}
				/>
				<span className="ai-flow-flowbar-right">
					<Button
						type="OUTLINE"
						small={true}
						label={fullscreen ? "Exit full screen" : "Full screen"}
						icon={fullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
						onClick={() => setFullscreen((prev) => !prev)}
					/>
				</span>
			</div>

			{currentFlow?.description && (
				<p className="ai-flow-flowbar-desc">{currentFlow.description}</p>
			)}

			{listError && (
				<div className="ai-panel-error" role="alert">
					{listError}
				</div>
			)}

			{flowId && (
				<ReactFlowProvider>
					<FlowBoard key={flowId} flowId={flowId} onMetaSaved={onMetaSaved} />
				</ReactFlowProvider>
			)}

			{showCreate && (
				<CreateFlowModal
					name={newName}
					description={newDesc}
					onName={setNewName}
					onDescription={setNewDesc}
					onCancel={() => !creating && setShowCreate(false)}
					onCreate={createNew}
					creating={creating}
				/>
			)}
		</div>
	)
}

export default FlowCanvas
